import { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { useForensicsEnvironmentsByVault, useStartForensicsVersion } from '../hooks'
import type { ForensicsEnvironment } from '@/types/entities'
import { Microscope } from 'lucide-react'

interface StartForensicsDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectId: string
    objectVersionId: number | null
    objectDescription?: string
    vaultId: number | null
}

export function StartForensicsDialog({
    open,
    onOpenChange,
    objectId,
    objectVersionId,
    objectDescription,
    vaultId,
}: StartForensicsDialogProps) {
    const [selectedEnvId, setSelectedEnvId] = useState<number | null>(null)
    const { data: environments, isLoading: envsLoading } = useForensicsEnvironmentsByVault(vaultId)
    const startVersion = useStartForensicsVersion()

    const envList: ForensicsEnvironment[] = Array.isArray(environments) ? environments : []
    const selectedEnv = envList.find(e => e.id === selectedEnvId)

    const handleStart = async () => {
        if (!selectedEnvId || !objectVersionId) return
        try {
            await startVersion.mutateAsync({
                envId: selectedEnvId,
                data: { objectId, objectVersionId },
            })
            onOpenChange(false)
            setSelectedEnvId(null)
        } catch {
            // Error handled in hook
        }
    }

    return (
        <Dialog open={open} onOpenChange={(val) => { onOpenChange(val); if (!val) setSelectedEnvId(null); }}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Microscope className="h-5 w-5" />
                        Start Forensics Analysis
                    </DialogTitle>
                    <DialogDescription>
                        Select a forensics environment to start this object version in an isolated security group.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs text-muted-foreground">Object ID</Label>
                            <p className="text-sm font-medium font-mono">{objectId}</p>
                        </div>
                        {objectDescription && (
                            <div>
                                <Label className="text-xs text-muted-foreground">Description</Label>
                                <p className="text-sm">{objectDescription}</p>
                            </div>
                        )}
                    </div>

                    <div>
                        <Label className="text-sm font-medium">Forensics Environment</Label>
                        {envsLoading ? (
                            <p className="text-sm text-muted-foreground mt-1">Loading environments...</p>
                        ) : envList.length === 0 ? (
                            <p className="text-sm text-muted-foreground mt-1">
                                No forensics environments available for this vault. Create one from the Forensics page.
                            </p>
                        ) : (
                            <Select
                                onValueChange={(val) => setSelectedEnvId(parseInt(val, 10))}
                                value={selectedEnvId ? String(selectedEnvId) : undefined}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Select environment" />
                                </SelectTrigger>
                                <SelectContent>
                                    {envList.map((env) => (
                                        <SelectItem key={env.id} value={String(env.id)}>
                                            {env.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    {selectedEnv && (
                        <div className="p-3 border rounded-lg bg-muted/50 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-xs text-muted-foreground">Security Group</Label>
                                    <p className="text-sm font-mono">{selectedEnv.securityGroupId}</p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">AWS Account</Label>
                                    <p className="text-sm font-mono">{selectedEnv.awsAccountId}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Label className="text-xs text-muted-foreground">Status:</Label>
                                <Badge variant={selectedEnv.status === 'active' ? 'default' : 'secondary'}>
                                    {selectedEnv.status}
                                </Badge>
                            </div>
                            {selectedEnv.servers && selectedEnv.servers.length > 0 && (
                                <div>
                                    <Label className="text-xs text-muted-foreground">Servers in this group: {selectedEnv.servers.length}</Label>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => { onOpenChange(false); setSelectedEnvId(null); }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleStart}
                        disabled={!selectedEnvId || !objectVersionId || startVersion.isPending}
                    >
                        {startVersion.isPending ? 'Starting...' : 'Start Forensics'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
